export type CompatibilityBlockReason =
  | 'insecure-context'
  | 'web-bluetooth-unavailable';

export type CompatibilityProbe = {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  hasWebBluetooth: boolean;
  isSecureContext: boolean;
};

export type CompatibilityResult = {
  supported: boolean;
  reason: CompatibilityBlockReason | null;
};

export function getCompatibilityProbe(): CompatibilityProbe {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    hasWebBluetooth:
      'bluetooth' in navigator && typeof navigator.bluetooth?.requestDevice === 'function',
    isSecureContext: window.isSecureContext,
  };
}

export function requiresBluefyOnIos(probe: CompatibilityProbe): boolean {
  return isIosDevice(probe) && !probe.hasWebBluetooth;
}

export function evaluateCompatibility(probe: CompatibilityProbe): CompatibilityResult {
  if (!probe.isSecureContext) {
    return { supported: false, reason: 'insecure-context' };
  }

  if (!probe.hasWebBluetooth) {
    return { supported: false, reason: 'web-bluetooth-unavailable' };
  }

  return { supported: true, reason: null };
}

export function getCompatibilityMessage(reason: CompatibilityBlockReason): string {
  if (reason === 'insecure-context') {
    return 'Bluetooth firmware updates only work from a secure page (HTTPS or localhost).';
  }
  return 'This browser does not provide the Web Bluetooth features required by this updater.';
}

function isIosDevice(probe: CompatibilityProbe): boolean {
  return (
    /iPad|iPhone|iPod/.test(probe.userAgent) ||
    (probe.platform === 'MacIntel' && probe.maxTouchPoints > 1)
  );
}
